import React, {
  useState, useMemo, useRef, useEffect, useCallback,
} from 'react';
import { FilterList } from '@mui/icons-material';
import {
  RESOURCES_BY_TYPE, canCreateResource, STATUS_BADGE, STATUS_LABEL,
} from './sandboxConfig';

function getItemLabel(item) {
  return item.order_number || item.purchase_order_number
    || item.invoice_number || item.sku || item.name || item.file_name || null;
}

function StatusFilterButton({ statuses, statusFilter, onFilterChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => statuses.length > 0 && setOpen(!open)}
        disabled={statuses.length === 0}
        className={`p-1.5 rounded-md border transition-colors ${
          // eslint-disable-next-line no-nested-ternary
          statusFilter
            ? 'border-accent-500 bg-accent-50 text-accent-600'
            : statuses.length === 0
              ? 'border-surface-200 text-surface-200 cursor-default'
              : 'border-surface-200 text-surface-400 hover:text-surface-600'
        }`}
        title="Filter by status"
      >
        <FilterList sx={{ fontSize: '14px' }} />
      </button>
      {open && statuses.length > 0 && (
        <div className="absolute right-0 top-full mt-1 z-20 bg-surface-0 border border-surface-200 rounded-lg shadow-lg py-1 min-w-[120px]">{/* eslint-disable-line max-len */}
          <button
            type="button"
            onClick={() => { onFilterChange(null); setOpen(false); }}
            className={`w-full text-left px-3 py-1.5 text-xs transition-colors ${
              !statusFilter
                ? 'bg-surface-50 font-medium text-surface-900'
                : 'text-surface-600 hover:bg-surface-50'
            }`}
          >
            All
          </button>
          {statuses.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => {
                onFilterChange(statusFilter === s ? null : s);
                setOpen(false);
              }}
              className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 transition-colors ${
                statusFilter === s
                  ? 'bg-surface-50 font-medium text-surface-900'
                  : 'text-surface-600 hover:bg-surface-50'
              }`}
            >
              <span className={`badge status-badge ${STATUS_BADGE[s] || 'badge-neutral'} text-[10px] py-0 px-1`}>
                {STATUS_LABEL[s] || s}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ResourceListSidebar({
  sandboxType,
  selectedResource,
  onResourceChange,
  rows,
  resourceCounts,
  selectedRowId,
  onRowSelect,
  onNewClick,
  onGenerateClick,
  hasConnection,
  loading,
  resourcesLoading,
  newLoading,
}) {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(null);

  const statuses = useMemo(() => {
    const set = new Set();
    rows.forEach((item) => { if (item.status) set.add(item.status); });
    return [...set].sort();
  }, [rows]);

  const filteredRows = useMemo(() => {
    let result = rows;
    if (statusFilter) {
      result = result.filter((item) => item.status === statusFilter);
    }
    if (search) {
      const q = search.toLowerCase();
      result = result.filter((item) => {
        const label = getItemLabel(item) || '';
        return item.id.toLowerCase().includes(q)
          || String(label).toLowerCase().includes(q);
      });
    }
    return result;
  }, [rows, search, statusFilter]);
  const listRef = useRef(null);

  const handleKeyDown = useCallback((e) => {
    if (!filteredRows.length) return;
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
    e.preventDefault();

    const currentIndex = filteredRows.findIndex((item) => item.id === selectedRowId);
    let nextIndex;
    if (e.key === 'ArrowDown') {
      nextIndex = currentIndex < filteredRows.length - 1 ? currentIndex + 1 : 0;
    } else {
      nextIndex = currentIndex > 0 ? currentIndex - 1 : filteredRows.length - 1;
    }
    onRowSelect(filteredRows[nextIndex]);

    const container = listRef.current;
    if (container) {
      const buttons = container.querySelectorAll('[data-item-id]');
      if (buttons[nextIndex]) {
        buttons[nextIndex].scrollIntoView({ block: 'nearest' });
        buttons[nextIndex].focus();
      }
    }
  }, [filteredRows, selectedRowId, onRowSelect]);

  const resources = [...(RESOURCES_BY_TYPE[sandboxType] || [])]
    .sort((a, b) => a.label.localeCompare(b.label));
  const resourceConfig = resources.find((r) => r.key === selectedResource);
  const resourceLabel = resourceConfig ? resourceConfig.label.replace(/s$/, '') : 'Resource';

  if (loading) {
    return (
      <div className="w-[560px] min-w-[560px] border-r border-surface-200 flex flex-col h-full bg-surface-0">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-xs text-surface-400">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-[560px] min-w-[560px] border-r border-surface-200 flex flex-col h-full bg-surface-0">
      {!hasConnection ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center py-8 px-3">
          <p className="text-xs text-surface-400 mb-3">Generate sandbox data to get started</p>
          <button type="button" className="btn btn-primary text-xs" onClick={onGenerateClick}>
            Generate Data
          </button>
        </div>
      ) : (
        <>
          {/* two-column content */}
          <div className="flex flex-1 min-h-0">
            {/* resource nav column */}
            <div className="w-[210px] min-w-[210px] max-w-[210px] border-r border-surface-100 overflow-y-auto py-2">
              <div className="text-[10px] font-medium text-surface-400 uppercase tracking-wider px-3 mb-1">Resources</div>
              {resources.map((r) => {
                const count = resourceCounts[r.key] ?? '';
                const isActive = r.key === selectedResource;
                return (
                  <button
                    key={r.key}
                    type="button"
                    onClick={() => { onResourceChange(r.key); setSearch(''); setStatusFilter(null); }}
                    className={`w-full flex items-center justify-between px-3 py-1.5 text-xs transition-colors ${
                      isActive
                        ? 'bg-surface-100 font-medium text-surface-900 border-l-2 border-accent-500 pl-2.5'
                        : 'text-surface-600 hover:bg-surface-50'
                    }`}
                  >
                    <span className="truncate">{r.label}</span>
                    {count !== '' && (
                      <span className="text-[10px] text-surface-400 ml-1">{count}</span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* items list column */}
            <div className="flex-1 flex flex-col min-h-0 min-w-0 overflow-hidden">
              <div className="flex gap-1.5 px-3 pt-2 pb-1 items-center">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search..."
                  className="input text-xs flex-1 min-w-0"
                />
                <StatusFilterButton
                  statuses={statuses}
                  statusFilter={statusFilter}
                  onFilterChange={setStatusFilter}
                />
              </div>
              {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
              <div className="flex-1 overflow-y-auto py-1 outline-none" ref={listRef} tabIndex={-1} onKeyDown={handleKeyDown}>
                {resourcesLoading && (
                  <div className="flex items-center justify-center py-6">
                    <div className="text-xs text-surface-400">Loading...</div>
                  </div>
                )}
                {!resourcesLoading && filteredRows.length === 0 && (
                  <p className="text-xs text-surface-400 px-3 py-4 text-center">
                    {search ? 'No matches' : 'No items'}
                  </p>
                )}
                {!resourcesLoading && filteredRows.map((item) => {
                  const label = getItemLabel(item);
                  const isSelected = item.id === selectedRowId;
                  return (
                    <button
                      key={item.id}
                      data-item-id={item.id}
                      type="button"
                      onClick={() => onRowSelect(item)}
                      className={`w-full flex items-center gap-1.5 px-3 py-1.5 text-left transition-colors ${
                        isSelected
                          ? 'bg-surface-100 border-l-2 border-accent-500 pl-2.5'
                          : 'hover:bg-surface-50'
                      }`}
                    >
                      <div className="flex flex-col flex-1 min-w-0">
                        <span className="text-xs font-medium text-surface-700 truncate">
                          {label || item.id}
                        </span>
                        {label && (
                          <span className="text-[11px] text-surface-400 font-mono truncate">
                            {item.id}
                          </span>
                        )}
                      </div>
                      {item.status && (
                        <span className={`badge status-badge ${STATUS_BADGE[item.status] || 'badge-neutral'} text-[10px] py-0 px-1 flex-shrink-0`}>
                          {STATUS_LABEL[item.status] || item.status}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* footer */}
          {canCreateResource(sandboxType, selectedResource) && (
            <div className="border-t border-surface-100 p-3">
              <button
                type="button"
                className="btn btn-secondary text-xs w-full"
                onClick={onNewClick}
                disabled={newLoading}
              >
                {newLoading ? 'Creating...' : `+ New ${resourceLabel}`}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default ResourceListSidebar;
